import { isAuthenticatedUser } from 'js/lib/user';

import {
  CONVERSATIONAL_ONBOARDING_QUERY_PARAM,
  type ConversationalOnboardingParamValue,
  LEARNING_GOAL_OPTIONS,
  type LEARNING_GOAL_OPTIONS_TYPE,
  ONBOARDING_BASE_URL,
} from 'bundles/onboarding-common/constants';
import {
  QUESTION_IDS,
  type QUESTION_IDS_TYPE,
  QUESTION_PATH_IDS,
  getQuestionPaths,
} from 'bundles/onboarding-common/constants/questionScheme';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useLearnerObjectLearningGoalData from 'bundles/onboarding-common/hooks/useLearnerObjectLearningGoalData';

const useBuildOnboardingUrl = ({
  isFirstTimeSeeingOnboarding,
  questionId,
  existingLearner,
  referrer,
  learningGoal: learningGoalFromProps,
  roleSelect: roleSelectFromProps,
  roleSelectAdvancer: roleSelectAdvancerFromProps,
  skillSelect: skillSelectFromProps,
  existingSearchParams,
  skipQueriesAndReturnBaseUrl = false,
  conversationalOnboardingParam,
}: {
  isFirstTimeSeeingOnboarding?: boolean;
  questionId?: QUESTION_IDS_TYPE;
  existingLearner?: boolean;
  referrer?: string;
  learningGoal?: LEARNING_GOAL_OPTIONS_TYPE | null;
  roleSelect?: Array<{ id: string }>;
  roleSelectAdvancer?: Array<{ id: string }>;
  skillSelect?: Array<{ id: string }>;
  existingSearchParams?: URLSearchParams;
  skipQueriesAndReturnBaseUrl?: boolean;
  conversationalOnboardingParam?: ConversationalOnboardingParamValue;
}) => {
  const isUnauthenticatedUser = !isAuthenticatedUser();
  const shouldSkipQuery =
    skipQueriesAndReturnBaseUrl || isFirstTimeSeeingOnboarding || isUnauthenticatedUser || !!learningGoalFromProps;
  const { value: learningGoalFromApi, loading } = useFetchData({
    useQueryData: useLearnerObjectLearningGoalData,
    skip: shouldSkipQuery,
    questionIdForLocalStorage: QUESTION_IDS.learningGoal,
  });
  const QUESTION_PATHS = getQuestionPaths();
  const learningGoalData = learningGoalFromApi || learningGoalFromProps;

  // Handle conversational onboarding variants - redirect to LIHP with query param
  if (conversationalOnboardingParam) {
    return {
      loading: false,
      onboardingUrl: `/?${CONVERSATIONAL_ONBOARDING_QUERY_PARAM}=${conversationalOnboardingParam}`,
    };
  }

  if (skipQueriesAndReturnBaseUrl) {
    return {
      loading: false,
      onboardingUrl: ONBOARDING_BASE_URL,
    };
  }

  const searchParams = existingSearchParams || new URLSearchParams();

  if (isFirstTimeSeeingOnboarding) {
    searchParams.set('isFirstTimeSeeingOnboarding', 'true');
  }

  if (existingLearner) {
    searchParams.set('existingLearner', 'true');
  }

  if (learningGoalData) {
    searchParams.set(QUESTION_IDS.learningGoal, learningGoalData);
  }

  if (referrer) {
    searchParams.set('referrer', referrer);
  }

  // Add role selections directly as URL parameters
  if (roleSelectFromProps && roleSelectFromProps.length > 0) {
    roleSelectFromProps.forEach((role) => {
      searchParams.append(`${QUESTION_IDS.roleSelect}[]`, role.id);
    });
  }

  if (roleSelectAdvancerFromProps && roleSelectAdvancerFromProps.length > 0) {
    roleSelectAdvancerFromProps.forEach((role) => {
      searchParams.append(`${QUESTION_IDS.roleSelectAdvancer}[]`, role.id);
    });
  }

  // Add skill selections directly as URL parameters
  if (skillSelectFromProps && skillSelectFromProps.length > 0) {
    skillSelectFromProps.forEach((skill) => {
      searchParams.append(`${QUESTION_IDS.skillSelect}[]`, skill.id);
    });
  }

  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  let questionIdParam: QUESTION_IDS_TYPE = QUESTION_PATHS[QUESTION_PATH_IDS.default][0];

  if (
    learningGoalData === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS &&
    questionId === QUESTION_IDS.roleSelect
  ) {
    // If the user is an advancer and we are directing to the role select question,
    // we want to make sure that they see the advancer role select question
    questionIdParam = QUESTION_IDS.roleSelectAdvancer;
  } else if (
    questionId === QUESTION_IDS.roleSelectAdvancer &&
    learningGoalData !== LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS
  ) {
    // If the user is not an advancer and we are directing to the advancer role select question,
    // we want to make sure that they see the default role select question
    questionIdParam = QUESTION_IDS.roleSelect;
  } else if (questionId) {
    questionIdParam = questionId;
  }

  const searchParamsString = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const onboardingUrl = `${ONBOARDING_BASE_URL}/${questionIdParam}${searchParamsString}`;

  return {
    loading,
    onboardingUrl,
  };
};

export default useBuildOnboardingUrl;
